<template>
  <div class="translations-table">
    <!-- Filters -->
    <div class="table-toolbar">
      <select-list
        v-model="paginaLocal"
        elem-id="translations-page-select"
        :itens-select="pageOptions"
        :placeholder="t('Selecionar página')"
        :label="t('Página')"
        data-testid="translations-page-select"
      />

      <select-list
        v-model="idiomaLocal"
        elem-id="translations-language-select"
        :itens-select="languageOptions"
        :placeholder="t('Todos os idiomas')"
        :label="t('Idioma')"
        data-testid="translations-language-select"
        :auto-select-first="false"
      />

      <input-text
        v-model="buscaLocal"
        elem-id="translations-search"
        :placeholder="t('Buscar')"
        data-testid="translations-search"
        @keyup.enter="emitSearch"
      />

      <select-list
        v-model="modoLocal"
        elem-id="translations-search-mode"
        :itens-select="modeOptions"
        data-testid="translations-search-mode"
      />

      <label class="checkbox-label" data-testid="translations-missing-label">
        <input
          type="checkbox"
          :checked="faltantes"
          data-testid="translations-missing-toggle"
          @change="$emit('update:faltantes', ($event.target as HTMLInputElement).checked)"
        />
        {{ t('Só faltantes') }}
      </label>

      <button
        class="btn btn-primary"
        data-testid="translations-btn-search"
        :disabled="loading"
        @click="emitSearch"
      >
        <i class="pi pi-search"></i>
        {{ t('Buscar') }}
      </button>
    </div>

    <!-- Actions -->
    <div class="table-toolbar">
      <button
        class="btn btn-primary"
        data-testid="translations-btn-add-line"
        @click="$emit('add-line')"
      >
        <i class="pi pi-plus"></i>
        {{ t('Criar chave') }}
      </button>

      <button
        class="btn btn-secondary"
        data-testid="translations-btn-retranslate"
        :disabled="!pagina || loading"
        @click="$emit('retranslate')"
      >
        <i class="pi pi-refresh"></i>
        {{ t('Retraduzir página') }}
      </button>

      <span class="hint">{{ t('Clique duplo para editar a tradução') }}</span>
      <span class="hint" data-testid="translations-total">{{ totalLabel }}</span>
    </div>

    <!-- The 422 refusal the proxy answers for a blank text: the save did NOT
         happen, so it is shown next to the table the user just edited, never
         as a transient toast. -->
    <p v-if="saveError" class="error-text" data-testid="translations-inline-error">
      {{ saveErrorLabel }}
    </p>

    <data-table
      ref="dataTableRef"
      testIdPrefix="translations"
      :config="datatableConfig"
      :data="tableRows"
      :refreshButton="{ show: true }"
      @refresh="$emit('refresh')"
      @inline-save="handleInlineSave"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { log } from '../../utils/DebugLogger'
import SelectList from '../../inputs/SelectList.vue'
import InputText from '../../inputs/InputText.vue'
import { TEXTO_VAZIO, type BuscaModo } from '../../composables/useTranslationsAdmin'
import type { Traducoes_ItemRow } from '../../contracts/Traducoes_ItemRow'
import type { Traducoes_IdiomaRow } from '../../contracts/Traducoes_IdiomaRow'
import type { Traducoes_PaginaRow } from '../../contracts/Traducoes_PaginaRow'

/** One rendered line of the table — a contract item flattened for <DataTable>. */
interface TranslationTableRow {
  row_key: string
  pagina: string
  chave: string
  traducao: string
  faltando: string
  [key: string]: unknown
}

interface InlineSavePayload {
  data?: TranslationTableRow
}

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    itens: Traducoes_ItemRow[]
    idiomas: Traducoes_IdiomaRow[]
    paginas: Traducoes_PaginaRow[]
    pagina: string
    idioma: string
    busca: string
    modo: BuscaModo
    faltantes: boolean
    total?: number
    mais?: boolean
    loading?: boolean
    saveError?: string | null
  }>(),
  { total: 0, mais: false, loading: false, saveError: null },
)

const emit = defineEmits<{
  'update:pagina': [string]
  'update:idioma': [string]
  'update:busca': [string]
  'update:modo': [BuscaModo]
  'update:faltantes': [boolean]
  'add-line': []
  retranslate: []
  refresh: []
  search: []
  'save-translation': [{ pagina: string; chave: string; texto: string }]
}>()

/** Local mirrors so v-model on the inputs stays one-way-out to the parent. */
const paginaLocal = ref<string>(props.pagina)
const idiomaLocal = ref<string>(props.idioma)
const buscaLocal = ref<string>(props.busca)
const modoLocal = ref<string>(props.modo)

watch(() => props.pagina, (v) => { paginaLocal.value = v })
watch(() => props.idioma, (v) => { idiomaLocal.value = v })
watch(() => props.busca, (v) => { buscaLocal.value = v })
watch(() => props.modo, (v) => { modoLocal.value = v })

watch(paginaLocal, (v) => emit('update:pagina', String(v ?? '')))
watch(idiomaLocal, (v) => emit('update:idioma', String(v ?? '')))
watch(buscaLocal, (v) => emit('update:busca', String(v ?? '')))
watch(modoLocal, (v) => emit('update:modo', (v === 'traducao' ? 'traducao' : 'padrao')))

const pageOptions = computed(() =>
  props.paginas.map((p) => ({ ID: p.nome ?? '', Name: p.nome ?? '' })),
)

const languageOptions = computed(() =>
  props.idiomas.map((l) => ({ ID: l.codigo ?? '', Name: l.nome ?? l.codigo ?? '' })),
)

const modeOptions = computed(() => [
  { ID: 'padrao', Name: t('Valor padrão') },
  { ID: 'traducao', Name: t('Tradução') },
])

/**
 * Which language's text the editable column shows. With no language picked the
 * table falls back to the first non-default one on the item, so the column is
 * never a mystery blank.
 */
const idiomaColuna = computed<string>(() => {
  if (props.idioma) return props.idioma
  const padrao = props.idiomas.find((l) => l.padrao)?.codigo
  const outro = props.idiomas.find((l) => l.codigo !== padrao)?.codigo
  return outro ?? padrao ?? ''
})

const tableRows = computed<TranslationTableRow[]>(() =>
  props.itens.map((item) => {
    const chave = item.chave ?? ''
    const pagina = item.pagina ?? ''
    const codigo = idiomaColuna.value
    return {
      row_key: `${pagina}__${chave}`,
      pagina,
      chave,
      traducao: item.traducoes?.[codigo] ?? '',
      faltando: (item.faltantes ?? []).includes(codigo) ? t('Sim') : '',
    }
  }),
)

const totalLabel = computed(() =>
  props.mais
    ? t('{count}+ resultados', { count: props.total })
    : t('{count} resultados', { count: props.total }),
)

const saveErrorLabel = computed(() =>
  props.saveError === TEXTO_VAZIO
    ? t('A tradução não pode ficar em branco — o serviço não apaga traduções.')
    : props.saveError,
)

const dataTableRef = ref<unknown>(null)

const tableColumns = computed(() => [
  { data: 'chave', title: t('Valor padrão'), filterType: 'text', editable: false, wrap: true, rowTestId: true },
  { data: 'traducao', title: t('Tradução'), filterType: 'text', editType: 'text', wrap: true },
  { data: 'faltando', title: t('Faltando'), filterType: 'select', editable: false },
])

const datatableConfig = computed(() => ({
  tableId: 'translationsDataTable',
  columns: tableColumns.value,
  dataKey: 'row_key',
  pagination: true,
  pageLength: 25,
  showFilters: true,
  filterDisplay: 'row',
  inlineEditing: true,
  inlineAddButton: false,
}))

function emitSearch(): void {
  emit('search')
}

const handleInlineSave = ({ data }: InlineSavePayload): void => {
  if (!data) {
    log('TranslationsTable', 'handleInlineSave', 'ERRO: payload sem data')
    return
  }
  emit('save-translation', {
    pagina: data.pagina,
    chave: data.chave,
    texto: data.traducao ?? '',
  })
}
</script>

<style scoped>
.translations-table {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.table-toolbar {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #333;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
}

.btn-primary {
  background-color: #0066cc;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #0052a3;
}

.btn-secondary {
  background-color: #eef2f7;
  color: #333;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #dde5ef;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.hint {
  color: #666;
  font-size: 12px;
  font-style: italic;
}

.error-text {
  color: #dc3545;
  font-size: 13px;
  margin: 0;
}
</style>
